import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { 
  Calculator, 
  FileText, 
  TrendingUp, 
  Users, 
  Building2, 
  PiggyBank,
  Home,
  BarChart3,
  Shield,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  Star,
  CheckCircle2,
  ArrowRight,
  Linkedin,
  Facebook,
  Clock,
  Zap,
  LineChart,
  CreditCard,
  FileCheck,
  Laptop,
  Globe,
  ChevronDown,
  Target,
  Briefcase,
  Receipt,
  Scale,
  Wallet,
  TrendingDown,
  PieChart,
  Calendar,
  HeadphonesIcon,
  Award,
  MessageCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ad4937fe-901e-43e7-9696-1069b20ab3dd/artifacts/fey37toe_HTCONSEILS%20%28mavi%29.jpg";
const WHATSAPP_NUMBER = "33789578187"; // Numéro sans le 0

// ============================================
// WHATSAPP BUTTON COMPONENT
// ============================================

const WhatsAppButton = () => {
  return (
    <a
      href={`https://wa.me/${WHATSAPP_NUMBER}?text=Bonjour, je souhaite obtenir des informations sur vos services d'expertise comptable.`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#128C7E] transition-colors hover:scale-110 transform duration-300"
      data-testid="whatsapp-button"
      aria-label="Nous contacter sur WhatsApp"
    >
      <svg 
        viewBox="0 0 24 24" 
        className="w-7 h-7 text-white fill-current"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// ============================================
// SHARED COMPONENTS
// ============================================

// Header Component - Toujours blanc
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Accueil', href: '/' },
    { name: 'Expert-comptable en ligne', href: '/expert-comptable-en-ligne' },
    { 
      name: 'Nos Packs', 
      href: '#',
      submenu: [
        { name: 'Création d\'entreprise', href: '/pack/creation-entreprise' },
        { name: 'Optimisation rémunération', href: '/pack/optimisation-remuneration' },
        { name: 'Location saisonnière', href: '/pack/location-saisonniere' },
        { name: 'Micro-entreprise', href: '/pack/micro-entreprise' },
        { name: 'Tableau de bord', href: '/pack/tableau-de-bord' },
      ]
    },
    { name: 'Contact', href: '/#contact' },
  ];

  return (
    <header 
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white shadow-lg py-3"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <img 
              src={LOGO_URL} 
              alt="HT Conseils - Expertise Comptable" 
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.submenu ? (
                <div 
                  key={link.name}
                  className="relative group"
                >
                  <button className="flex items-center gap-1 text-sm font-semibold tracking-wide uppercase transition-all duration-300 hover:text-[#0579c4] text-[#0b324e] py-4">
                    {link.name}
                    <ChevronDown size={16} className="group-hover:rotate-180 transition-transform duration-200" />
                  </button>
                  <div className="absolute top-full left-0 pt-0 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <div className="bg-white shadow-xl border-t-2 border-[#0579c4] py-2">
                      {link.submenu.map((sublink) => (
                        <Link
                          key={sublink.name}
                          to={sublink.href}
                          className="block px-4 py-3 text-sm text-[#0b324e] hover:bg-[#f8fafc] hover:text-[#0579c4] transition-colors"
                        >
                          {sublink.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-sm font-semibold tracking-wide uppercase transition-all duration-300 hover:text-[#0579c4] text-[#0b324e]"
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:0789578187" className="flex items-center gap-2 text-sm font-semibold text-[#0b324e]">
              <Phone size={16} />
              07 89 57 81 87
            </a>
            <a href="/#contact" className="btn-primary" data-testid="header-cta">
              Prendre RDV
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? (
              <X className="text-[#0b324e]" size={28} />
            ) : (
              <Menu className="text-[#0b324e]" size={28} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4" data-testid="mobile-nav">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                link.submenu ? (
                  <div key={link.name}>
                    <div className="px-4 py-2 text-sm font-semibold text-[#0b324e] uppercase">{link.name}</div>
                    {link.submenu.map((sublink) => (
                      <Link
                        key={sublink.name}
                        to={sublink.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block px-6 py-2 text-sm text-gray-600 hover:text-[#0579c4]"
                      >
                        {sublink.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="px-4 py-2 text-sm font-semibold text-[#0b324e] uppercase"
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <div className="px-4 pt-4 border-t border-gray-200">
                <a href="tel:0789578187" className="flex items-center gap-2 text-[#0579c4] font-semibold mb-3">
                  <Phone size={18} /> 07 89 57 81 87
                </a>
                <a href="/#contact" className="btn-primary w-full justify-center" onClick={() => setIsMobileMenuOpen(false)}>
                  Prendre RDV
                </a>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  const cities = [
    { name: 'Paris', href: '/expert-comptable-paris' },
    { name: 'Lyon', href: '/expert-comptable-lyon' },
    { name: 'Marseille', href: '/expert-comptable-marseille' },
    { name: 'Strasbourg', href: '/expert-comptable-strasbourg' },
    { name: 'Bordeaux', href: '/expert-comptable-bordeaux' },
    { name: 'Toulouse', href: '/expert-comptable-toulouse' },
  ];

  const packs = [
    { name: 'Création d\'entreprise', href: '/pack/creation-entreprise' },
    { name: 'Optimisation rémunération', href: '/pack/optimisation-remuneration' },
    { name: 'Location saisonnière', href: '/pack/location-saisonniere' },
    { name: 'Micro-entreprise', href: '/pack/micro-entreprise' },
    { name: 'Tableau de bord', href: '/pack/tableau-de-bord' },
  ];

  return (
    <footer className="bg-[#0b324e] pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <img src={LOGO_URL} alt="HT Conseils" className="h-16 w-auto mb-4 bg-white p-2 rounded" />
            <p className="text-white/70 mb-4 text-sm">
              Cabinet d'expertise comptable 100% digital propulsé par Pennylane. 
              Conseil fiscal & pilotage pour dirigeants ambitieux partout en France.
            </p>
            <p className="text-white/50 text-xs">
              Expert-comptable inscrit à l'Ordre des Experts-Comptables de la région Grand Est
            </p>
            <div className="flex gap-3 mt-4">
              <a href="https://www.linkedin.com/in/ht-conseils-654a02233/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-[#0579c4] transition-colors">
                <Linkedin className="w-4 h-4 text-white" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=100078781342085" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 flex items-center justify-center hover:bg-[#0579c4] transition-colors">
                <Facebook className="w-4 h-4 text-white" />
              </a>
            </div>
          </div>

          {/* Nos Packs */}
          <div>
            <h4 className="text-white font-semibold mb-4">Nos Packs</h4>
            <ul className="space-y-2">
              {packs.map((pack) => (
                <li key={pack.name}>
                  <Link to={pack.href} className="text-white/70 hover:text-[#0579c4] transition-colors text-sm">
                    {pack.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Villes */}
          <div>
            <h4 className="text-white font-semibold mb-4">Expert-comptable</h4>
            <ul className="space-y-2">
              {cities.map((city) => (
                <li key={city.name}>
                  <Link to={city.href} className="text-white/70 hover:text-[#0579c4] transition-colors text-sm">
                    {city.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/expert-comptable-vallee-bruche" className="text-white/70 hover:text-[#0579c4] transition-colors text-sm">
                  Vallée de la Bruche
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="tel:0789578187" className="flex items-center gap-2 text-white/70 hover:text-[#0579c4]">
                  <Phone size={14} /> 07 89 57 81 87
                </a>
              </li>
              <li>
                <a href="mailto:contact@htconseils.fr" className="flex items-center gap-2 text-white/70 hover:text-[#0579c4]">
                  <Mail size={14} /> contact@htconseils.fr
                </a>
              </li>
              <li className="flex items-start gap-2 text-white/70">
                <MapPin size={14} className="mt-1 flex-shrink-0" />
                <span>3 rue du Commandant Clerc<br />67190 Mutzig, Alsace</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} HT Conseils. Tous droits réservés.
          </p>
          <div className="flex gap-6">
            <Link to="/mentions-legales" className="text-white/50 hover:text-white text-sm transition-colors">Mentions légales</Link>
            <Link to="/politique-confidentialite" className="text-white/50 hover:text-white text-sm transition-colors">Politique de confidentialité</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// HOME PAGE COMPONENTS
// ============================================

// Hero Section
const Hero = () => {
  return (
    <section 
      id="accueil" 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-20"
    >
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?auto=format&fit=crop&w=1920&q=80')`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b324e]/95 via-[#0b324e]/85 to-[#0579c4]/70" />
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="max-w-3xl">
          <div className="animate-fadeInUp opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-semibold tracking-widest uppercase mb-6">
              <Laptop size={16} /> Cabinet 100% Digital
            </span>
          </div>
          
          <div className="animate-fadeInUp opacity-0 mb-4" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            <span className="text-2xl sm:text-3xl font-bold text-white tracking-wide">
              HT CONSEILS
            </span>
            <span className="text-2xl sm:text-3xl font-light text-white/80 ml-2">
              Expertise Comptable
            </span>
          </div>
          
          <h1 className="animate-fadeInUp opacity-0 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            Expert-comptable{' '}
            <span className="text-white bg-[#0579c4] px-3 py-1">100% digital</span>
            <br />
            <span className="text-3xl sm:text-4xl lg:text-5xl font-normal">Conseil fiscal & pilotage pour dirigeants ambitieux</span>
          </h1>
          
          <p className="animate-fadeInUp opacity-0 text-lg sm:text-xl text-white/90 mb-6 leading-relaxed max-w-2xl" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
            Cabinet propulsé par <strong>Pennylane</strong> : trésorerie en temps réel, facturation intégrée, 
            automatisations comptables. Intervention partout en France.
          </p>

          <div className="animate-fadeInUp opacity-0 flex flex-wrap gap-4 mb-8 text-white/80 text-sm" style={{ animationDelay: '0.7s', animationFillMode: 'forwards' }}>
            <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#0579c4]" /> Réactivité 24-48h</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#0579c4]" /> Onboarding simple</span>
            <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#0579c4]" /> Tableaux de bord clairs</span>
          </div>
          
          <div className="animate-fadeInUp opacity-0 flex flex-col sm:flex-row gap-4" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
            <a href="#contact" className="btn-white" data-testid="hero-contact-btn">
              Contactez-nous
              <ArrowRight size={18} />
            </a>
            <a href="#services" className="btn-secondary text-white border-white hover:bg-white hover:text-[#0b324e]" data-testid="hero-services-btn">
              Découvrir nos services
            </a>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white rounded-full" />
        </div>
      </div>
    </section>
  );
};

// Stats Section
const Stats = () => {
  const stats = [
    { value: '15+', label: 'Années d\'expérience' },
    { value: '200+', label: 'Clients accompagnés' },
    { value: '100%', label: 'Satisfaction client' },
    { value: '24h', label: 'Réactivité garantie' },
  ];

  return (
    <section className="relative -mt-16 z-20" data-testid="stats-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="bg-white shadow-2xl grid grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className={`p-8 lg:p-10 text-center ${
                index < stats.length - 1 ? 'border-r border-gray-100' : ''
              } ${index < 2 ? 'border-b lg:border-b-0 border-gray-100' : ''}`}
            >
              <div className="text-3xl lg:text-4xl font-bold text-[#0579c4] mb-2">{stat.value}</div>
              <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pennylane Section
const PennylaneSection = () => {
  const benefits = [
    { icon: LineChart, title: 'Vision trésorerie temps réel', desc: 'Compta + banque + facturation centralisés' },
    { icon: Zap, title: 'Automatisations intelligentes', desc: 'Réduisez les tâches manuelles et erreurs' },
    { icon: BarChart3, title: 'Pilotage par tableaux de bord', desc: 'Marges, TVA, prévisionnel en un clic' },
    { icon: CreditCard, title: 'Intégrations complètes', desc: 'Banques, e-commerce, outils de facturation' },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-[#f8fafc] to-white" data-testid="pennylane-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-[#0579c4] text-sm font-semibold tracking-widest uppercase">Propulsé par Pennylane</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0b324e] mt-4 mb-4">
            Pourquoi un cabinet digital change tout
          </h2>
          <p className="text-gray-600">
            Fini les allers-retours de documents, les tableurs Excel et le stress de fin d'année. 
            Avec Pennylane, vous pilotez votre entreprise en temps réel.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="bg-white p-6 shadow-lg hover:shadow-xl transition-shadow border-t-4 border-[#0579c4]"
              data-testid={`pennylane-benefit-${index}`}
            >
              <div className="w-12 h-12 bg-[#0579c4]/10 flex items-center justify-center mb-4">
                <benefit.icon className="w-6 h-6 text-[#0579c4]" />
              </div>
              <h3 className="text-lg font-bold text-[#0b324e] mb-2">{benefit.title}</h3>
              <p className="text-gray-600 text-sm">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const Services = () => {
  const services = [
    {
      icon: Calculator,
      title: 'Expertise Comptable',
      description: 'Tenue comptable, établissement des bilans et situations intermédiaires. Une vision claire de votre activité.',
    },
    {
      icon: FileText,
      title: 'Déclarations Fiscales',
      description: 'Gestion complète de vos obligations fiscales : TVA, IS, IR, CFE. Optimisation et conformité garanties.',
    },
    {
      icon: Users,
      title: 'Paie & Social',
      description: 'Gestion des bulletins de paie, déclarations sociales et accompagnement RH pour vos salariés.',
    },
    {
      icon: Building2,
      title: 'Création d\'Entreprise',
      description: 'Accompagnement complet : choix du statut juridique, business plan, formalités de création.',
    },
    {
      icon: TrendingUp,
      title: 'Conseil Fiscal',
      description: 'Stratégie fiscale personnalisée pour optimiser votre imposition en toute légalité.',
    },
    {
      icon: BarChart3,
      title: 'Gestion & Prévisionnel',
      description: 'Budgets prévisionnels, tableaux de bord et suivi de gestion pour piloter votre activité.',
    },
  ];

  return (
    <section id="services" className="py-20 bg-[#f8fafc]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#0579c4] text-sm font-semibold tracking-widest uppercase">Nos Services</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0b324e] mt-4 mb-6">
            Un accompagnement complet pour votre entreprise
          </h2>
          <p className="text-gray-600 text-lg">
            De la tenue comptable au conseil stratégique, nous vous apportons des solutions adaptées à vos besoins.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className="bg-white p-8 card-hover group cursor-pointer"
              data-testid={`service-card-${index}`}
            >
              <div className="w-14 h-14 bg-[#0579c4]/10 flex items-center justify-center mb-6 group-hover:bg-[#0579c4] transition-colors duration-300">
                <service.icon className="w-7 h-7 text-[#0579c4] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-[#0b324e] mb-3">{service.title}</h3>
              <p className="text-gray-600 leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Expertise Section (Missions Annexes)
const Expertise = () => {
  const expertises = [
    {
      icon: PiggyBank,
      title: 'Optimisation Rémunération Dirigeant',
      description: 'Maximisez vos revenus nets grâce à une stratégie de rémunération optimisée : arbitrage salaire/dividendes, protection sociale, épargne retraite.',
      features: ['Simulation personnalisée', 'Arbitrage fiscal', 'Protection sociale optimale'],
      link: '/pack/optimisation-remuneration',
    },
    {
      icon: Home,
      title: 'Spécialiste Location Meublée',
      description: 'Expert LMNP et LMP, nous vous accompagnons dans la gestion fiscale et comptable de vos investissements en location meublée.',
      features: ['Régime réel optimisé', 'Amortissements', 'Déclarations spécifiques'],
      link: '/pack/location-saisonniere',
    },
    {
      icon: BarChart3,
      title: 'Tableaux de Bord de Gestion',
      description: 'Pilotez votre activité avec des indicateurs clés sur-mesure. Anticipez, décidez, performez.',
      features: ['KPIs personnalisés', 'Reporting mensuel', 'Aide à la décision'],
      link: '/pack/tableau-de-bord',
    },
  ];

  return (
    <section id="expertise" className="py-20 bg-white" data-testid="expertise-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <span className="text-[#0579c4] text-sm font-semibold tracking-widest uppercase">Expertises Spécialisées</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0b324e] mt-4 mb-6">
              Des missions sur-mesure pour vos projets
            </h2>
            <p className="text-gray-600 text-lg mb-10">
              Au-delà de la comptabilité traditionnelle, nous développons des expertises pointues 
              pour répondre à vos enjeux spécifiques et vous accompagner vers la réussite.
            </p>

            <div className="space-y-8">
              {expertises.map((item, index) => (
                <Link key={index} to={item.link} className="flex gap-5 group" data-testid={`expertise-item-${index}`}>
                  <div className="flex-shrink-0 w-12 h-12 bg-[#0b324e] flex items-center justify-center group-hover:bg-[#0579c4] transition-colors">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#0b324e] mb-2 group-hover:text-[#0579c4] transition-colors">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-3">{item.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {item.features.map((feature, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-xs font-medium text-[#0579c4] bg-[#0579c4]/10 px-3 py-1">
                          <CheckCircle2 size={12} /> {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1758518729912-bf3a84c400e0?auto=format&fit=crop&w=800&q=80" 
                alt="Conseil expertise comptable"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b324e]/50 to-transparent" />
            </div>
            
            {/* Floating Card */}
            <div className="absolute -bottom-8 -left-8 bg-white p-8 shadow-2xl max-w-xs">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-[#0579c4] flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#0b324e]">100%</div>
                  <div className="text-sm text-gray-600">Conformité</div>
                </div>
              </div>
              <p className="text-gray-600 text-sm">
                Membre de l'Ordre des Experts-Comptables de la région Grand Est
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section - Sans image
const About = () => {
  return (
    <section className="py-20 bg-[#f8fafc]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Valeurs */}
          <div>
            <span className="text-[#0579c4] text-sm font-semibold tracking-widest uppercase">À Propos</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0b324e] mt-4 mb-6">
              Un cabinet à taille humaine, proche de vous
            </h2>
            <p className="text-gray-600 text-lg mb-6 leading-relaxed">
              HT Conseils est un cabinet d'expertise comptable implanté en Alsace, dans la vallée de la Bruche, 
              à Mutzig. Expert-comptable inscrit auprès de l'Ordre des Experts-Comptables de la région Grand Est.
            </p>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Notre positionnement entre la vallée de la Bruche et l'Eurométropole nous permet d'être au plus 
              proche de nos clients. Le cabinet se distingue par sa <strong>proximité</strong> et sa <strong>réactivité</strong>.
            </p>
            
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-[#0579c4] flex-shrink-0" />
                <span className="text-[#0b324e] font-medium">Proximité client</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-[#0579c4] flex-shrink-0" />
                <span className="text-[#0b324e] font-medium">Réactivité garantie</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-[#0579c4] flex-shrink-0" />
                <span className="text-[#0b324e] font-medium">Expertise métier</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-[#0579c4] flex-shrink-0" />
                <span className="text-[#0b324e] font-medium">Cabinet digital</span>
              </div>
            </div>

            <a href="#contact" className="btn-primary" data-testid="about-cta">
              Nous contacter
              <ArrowRight size={18} />
            </a>
          </div>

          {/* Right - Engagements */}
          <div className="space-y-6">
            <div className="bg-white p-8 shadow-lg border-l-4 border-[#0579c4]">
              <h3 className="text-xl font-bold text-[#0b324e] mb-3">Notre engagement</h3>
              <p className="text-gray-600">
                Accompagner chaque entrepreneur avec la même exigence et le même professionnalisme, 
                qu'il soit à Mutzig ou à Paris. Grâce à notre modèle 100% digital, nous offrons 
                un service premium accessible partout en France.
              </p>
            </div>
            
            <div className="bg-[#0b324e] p-8 text-white">
              <h3 className="text-xl font-bold mb-3">Pourquoi nous choisir ?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#0579c4] flex-shrink-0 mt-1" size={18} />
                  <span>Réponse garantie sous 24-48h</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#0579c4] flex-shrink-0 mt-1" size={18} />
                  <span>Trésorerie en temps réel avec Pennylane</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#0579c4] flex-shrink-0 mt-1" size={18} />
                  <span>Un interlocuteur unique qui connaît votre dossier</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="text-[#0579c4] flex-shrink-0 mt-1" size={18} />
                  <span>Tarifs transparents, sans surprise</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 shadow-lg flex items-center gap-4">
              <div className="w-16 h-16 bg-[#0579c4]/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-8 h-8 text-[#0579c4]" />
              </div>
              <div>
                <div className="font-bold text-[#0b324e]">Membre de l'Ordre</div>
                <div className="text-gray-600 text-sm">Experts-Comptables région Grand Est</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section - avec Xavier
const Testimonials = () => {
  const testimonials = [
    {
      content: "Conseils, réactivité, compétences, disponibilité et à l'écoute, je ne peux que recommander ce cabinet.",
      author: "Virginie L.",
      role: "Gérante d'un magasin spécialisé en jardinage et bricolage",
      rating: 5,
    },
    {
      content: "Je travaille avec le cabinet HT Conseils depuis 2019 et j'en suis pleinement satisfait. En et dès le début de la mission une remise à plat complète de la comptabilité de ma société a été réalisée à l'initiative de Mr Tuncer, ceci a permis de repartir sur des bases saines mais également de détecter quelques erreurs réalisées par le passé qui ont pu être corrigées.",
      author: "Damien L.",
      role: "Gérant de société dans l'Ingénierie & Conseil en Génie Climatique",
      rating: 5,
    },
    {
      content: "C'est un expert-comptable très réactif, à l'écoute. Il prend le temps d'expliquer les sujets que je ne maîtrise pas. Collaborer avec le cabinet HT CONSEILS est un très bon choix.",
      author: "Mikail I.",
      role: "Président de Société dans l'Automatismes et de contrôle d'accès",
      rating: 5,
    },
    {
      content: "M. TUNCER est investi dans son travail, et toujours disponible. Travail rapide et efficace. Je recommande le cabinet HT CONSEILS.",
      author: "Xavier G.",
      role: "Kinésithérapeute libéral",
      rating: 5,
    },
  ];

  return (
    <section id="temoignages" className="py-20 bg-[#0b324e]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#0579c4] text-sm font-semibold tracking-widest uppercase">Témoignages</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 mb-6">
            Ce que disent nos clients
          </h2>
          <p className="text-white/70 text-lg">
            La satisfaction de nos clients est notre priorité. Découvrez leurs retours d'expérience.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white/5 backdrop-blur-sm p-8 border border-white/10 hover:bg-white/10 transition-all duration-300"
              data-testid={`testimonial-card-${index}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={18} className="fill-[#0579c4] text-[#0579c4]" />
                ))}
              </div>
              <p className="text-white/90 text-lg leading-relaxed mb-6 italic">
                "{testimonial.content}"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#0579c4] flex items-center justify-center text-white font-bold text-lg">
                  {testimonial.author.charAt(0)}
                </div>
                <div>
                  <div className="text-white font-semibold">{testimonial.author}</div>
                  <div className="text-white/60 text-sm">{testimonial.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="relative py-24 overflow-hidden" data-testid="cta-section">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1758518731468-98e90ffd7430?auto=format&fit=crop&w=1920&q=80')`
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0b324e]/95 to-[#0579c4]/90" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
          Prêt à développer votre entreprise ?
        </h2>
        <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
          Contactez-nous dès aujourd'hui pour un premier échange gratuit et sans engagement. 
          Ensemble, construisons votre succès.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#contact" className="btn-white" data-testid="cta-contact">
            Prendre rendez-vous
            <ArrowRight size={18} />
          </a>
          <a href="tel:0789578187" className="btn-secondary text-white border-white hover:bg-white hover:text-[#0b324e]" data-testid="cta-phone">
            <Phone size={18} />
            07 89 57 81 87
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
    
    setIsSubmitting(false);
    setTimeout(() => setSubmitStatus(null), 5000);
  };

  return (
    <section id="contact" className="py-20 bg-[#f8fafc]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <span className="text-[#0579c4] text-sm font-semibold tracking-widest uppercase">Contact</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0b324e] mt-4 mb-6">
              Parlons de votre projet
            </h2>
            <p className="text-gray-600 text-lg mb-10 leading-relaxed">
              Vous souhaitez échanger sur un projet de création, de développement ou de transmission d'entreprise ? 
              Contactez-nous pour un premier échange sans engagement.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0579c4] flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Téléphone</div>
                  <a href="tel:0789578187" className="text-xl font-semibold text-[#0b324e] hover:text-[#0579c4] transition-colors">
                    07 89 57 81 87
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0579c4] flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Email</div>
                  <a href="mailto:contact@htconseils.fr" className="text-xl font-semibold text-[#0b324e] hover:text-[#0579c4] transition-colors">
                    contact@htconseils.fr
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#0579c4] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Adresse</div>
                  <div className="text-xl font-semibold text-[#0b324e]">
                    3 rue du Commandant Clerc<br />
                    67190 Mutzig, Alsace
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4">
              <a 
                href="https://www.linkedin.com/in/ht-conseils-654a02233/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 bg-[#0b324e] flex items-center justify-center hover:bg-[#0579c4] transition-colors"
              >
                <Linkedin className="w-5 h-5 text-white" />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=100078781342085" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 bg-[#0b324e] flex items-center justify-center hover:bg-[#0579c4] transition-colors"
              >
                <Facebook className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white p-8 lg:p-10 shadow-xl">
            <h3 className="text-2xl font-bold text-[#0b324e] mb-6">Envoyez-nous un message</h3>
            
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700">
                Merci ! Votre message a été envoyé avec succès. Nous vous recontacterons rapidement.
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700">
                Une erreur est survenue. Veuillez réessayer ou nous contacter directement par téléphone.
              </div>
            )}

            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#0579c4] focus:ring-1 focus:ring-[#0579c4] outline-none transition-colors"
                    placeholder="Votre nom"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#0579c4] focus:ring-1 focus:ring-[#0579c4] outline-none transition-colors"
                    placeholder="votre@email.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#0579c4] focus:ring-1 focus:ring-[#0579c4] outline-none transition-colors"
                    placeholder="06 XX XX XX XX"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Entreprise</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#0579c4] focus:ring-1 focus:ring-[#0579c4] outline-none transition-colors"
                    placeholder="Nom de votre entreprise"
                    data-testid="input-company"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Sujet *</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-200 focus:border-[#0579c4] focus:ring-1 focus:ring-[#0579c4] outline-none transition-colors bg-white"
                  data-testid="input-subject"
                >
                  <option value="">Sélectionnez un sujet</option>
                  <option value="creation">Création d'entreprise</option>
                  <option value="comptabilite">Expertise comptable</option>
                  <option value="fiscal">Conseil fiscal</option>
                  <option value="paie">Paie et social</option>
                  <option value="lmnp">Location meublée (LMNP/LMP)</option>
                  <option value="optimisation">Optimisation rémunération</option>
                  <option value="autre">Autre demande</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-200 focus:border-[#0579c4] focus:ring-1 focus:ring-[#0579c4] outline-none transition-colors resize-none"
                  placeholder="Décrivez votre projet ou votre demande..."
                  data-testid="input-message"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// HOME PAGE
// ============================================

const HomePage = () => {
  return (
    <>
      <Hero />
      <Stats />
      <PennylaneSection />
      <Services />
      <Expertise />
      <About />
      <Testimonials />
      <CTASection />
      <Contact />
    </>
  );
};

// ============================================
// EXPERT COMPTABLE EN LIGNE PAGE
// ============================================

const ExpertComptableEnLignePage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="bg-[#0b324e] pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-semibold tracking-widest uppercase mb-6">
              <Globe size={16} /> France entière
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Expert-comptable en ligne
              <span className="block text-[#0579c4] mt-2">Conseil fiscal & pilotage pour dirigeants exigeants</span>
            </h1>
            <p className="text-white/80 text-lg mb-8">
              Cabinet 100% digital propulsé par Pennylane. Rémunération dirigeant optimisée, 
              LMNP, Création d'entreprise – accompagnement premium partout en France.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/#contact" className="btn-white">
                Prendre RDV
                <ArrowRight size={18} />
              </a>
              <a href="tel:0789578187" className="btn-secondary text-white border-white hover:bg-white hover:text-[#0b324e]">
                <Phone size={18} />
                07 89 57 81 87
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages Digital */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0b324e] mb-8 text-center">
            Pourquoi un cabinet 100% digital propulsé par Pennylane ?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: 'Réactivité 24-48h', desc: 'Réponses rapides, pas d\'attente' },
              { icon: LineChart, title: 'Trésorerie temps réel', desc: 'Vision claire de votre cash' },
              { icon: Zap, title: 'Automatisations', desc: 'Moins de saisie, moins d\'erreurs' },
              { icon: Laptop, title: 'Accès partout', desc: 'Votre compta accessible 24/7' },
            ].map((item, i) => (
              <div key={i} className="text-center p-6">
                <div className="w-16 h-16 bg-[#0579c4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-[#0579c4]" />
                </div>
                <h3 className="font-bold text-[#0b324e] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0b324e] mb-8 text-center">Nos services premium</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: PiggyBank, title: 'Optimisation rémunération dirigeant', desc: 'Comparatifs salaire/dividendes, protection sociale, simulations personnalisées', link: '/pack/optimisation-remuneration' },
              { icon: Building2, title: 'Création & Transformation', desc: 'SASU, EURL, SARL, SAS – formalités complètes et mise en place Pennylane', link: '/pack/creation-entreprise' },
              { icon: Home, title: 'LMNP & Location saisonnière', desc: 'Micro-BIC vs réel, amortissements, liasse fiscale, Airbnb', link: '/pack/location-saisonniere' },
              { icon: FileCheck, title: 'Micro → Réel / Transformation', desc: 'Bascule optimisée, arbitrages fiscaux et sociaux', link: '/pack/micro-entreprise' },
              { icon: BarChart3, title: 'Pilotage mensuel', desc: 'Tableaux de bord, budget, alertes, RDV réguliers', link: '/pack/tableau-de-bord' },
              { icon: Calculator, title: 'Expertise comptable', desc: 'Tenue, bilan, déclarations, situations intermédiaires', link: '/#services' },
            ].map((service, i) => (
              <Link key={i} to={service.link} className="bg-white p-6 shadow hover:shadow-lg transition-shadow group">
                <div className="w-12 h-12 bg-[#0579c4] flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-[#0b324e] mb-2 group-hover:text-[#0579c4] transition-colors">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0579c4]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Prêt à passer au 100% digital ?</h2>
          <p className="text-white/90 mb-8">Premier échange gratuit et sans engagement</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#contact" className="btn-white">
              Prendre rendez-vous
              <ArrowRight size={18} />
            </a>
            <a href="tel:0789578187" className="btn-secondary text-white border-white hover:bg-white hover:text-[#0579c4]">
              <Phone size={18} />
              07 89 57 81 87
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============================================
// PACK PAGES - Avec contenu complet
// ============================================

const PackCreationEntreprise = () => {
  const etapes = [
    { title: 'Diagnostic initial', desc: 'Analyse de votre projet, choix du statut juridique optimal (SASU, EURL, SARL, SAS)' },
    { title: 'Business plan', desc: 'Élaboration du prévisionnel financier et validation de la viabilité' },
    { title: 'Formalités juridiques', desc: 'Rédaction des statuts, immatriculation, dépôt du capital' },
    { title: 'Mise en place Pennylane', desc: 'Configuration de votre espace, connexion bancaire, facturation' },
    { title: 'Formation & accompagnement', desc: 'Prise en main des outils, premiers pas comptables' },
  ];

  return (
    <div className="min-h-screen pt-20">
      <section className="bg-[#0b324e] pt-16 pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-semibold tracking-widest uppercase mb-6">
            <Building2 size={16} /> Pack Premium
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">Création d'Entreprise</h1>
          <p className="text-white/80 text-lg mb-8">
            SASU, EURL, SARL, SAS – Un accompagnement complet de l'idée à l'immatriculation, 
            avec mise en place immédiate de Pennylane pour démarrer sur de bonnes bases.
          </p>
          <a href="/#contact" className="btn-white">
            Demander un devis
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#0b324e] mb-6">Ce que comprend le pack</h2>
              <ul className="space-y-4">
                {[
                  'Étude comparative des statuts juridiques',
                  'Rédaction personnalisée des statuts',
                  'Prévisionnel financier sur 3 ans',
                  'Dépôt du capital et formalités CFE',
                  'Immatriculation et obtention du KBIS',
                  'Paramétrage complet Pennylane',
                  'Formation à la facturation et gestion',
                  'Accompagnement post-création (3 mois)',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#0579c4] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#0b324e] mb-6">Les étapes</h2>
              <div className="space-y-4">
                {etapes.map((etape, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-[#f8fafc]">
                    <div className="w-8 h-8 bg-[#0579c4] text-white font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0b324e]">{etape.title}</h3>
                      <p className="text-gray-600 text-sm">{etape.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0579c4]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Prêt à lancer votre entreprise ?</h2>
          <p className="text-white/90 mb-8">Contactez-nous pour un premier échange gratuit</p>
          <a href="/#contact" className="btn-white">
            Prendre rendez-vous
            <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
};

const PackOptimisationRemuneration = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="bg-[#0b324e] pt-16 pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-semibold tracking-widest uppercase mb-6">
            <PiggyBank size={16} /> Pack Premium
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">Optimisation de la Rémunération</h1>
          <p className="text-white/80 text-lg mb-8">
            Maximisez vos revenus nets en toute légalité. Arbitrage salaire/dividendes, 
            protection sociale optimale, stratégie fiscale personnalisée.
          </p>
          <a href="/#contact" className="btn-white">
            Demander une étude
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#0b324e] mb-6">Notre approche</h2>
              <p className="text-gray-600 mb-6">
                Chaque situation est unique. Nous analysons en détail votre profil (statut, revenus, 
                situation familiale, projets) pour construire une stratégie de rémunération sur-mesure.
              </p>
              <ul className="space-y-4">
                {[
                  'Analyse complète de votre situation actuelle',
                  'Simulation comparée salaire vs dividendes',
                  'Optimisation des charges sociales',
                  'Stratégie de protection sociale (retraite, prévoyance)',
                  'Arbitrage IS/IR selon votre situation',
                  'Plan d\'épargne salariale si pertinent',
                  'Suivi et ajustements annuels',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#0579c4] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#f8fafc] p-8">
              <h2 className="text-2xl font-bold text-[#0b324e] mb-6">Les questions clés</h2>
              <div className="space-y-6">
                {[
                  { q: 'Combien me verser en salaire ?', a: 'Dépend de vos besoins de trésorerie personnelle et de votre couverture sociale souhaitée.' },
                  { q: 'Quand distribuer des dividendes ?', a: 'Après l\'AG d\'approbation des comptes, en tenant compte de la flat tax ou du barème progressif.' },
                  { q: 'Comment optimiser ma retraite ?', a: 'PER, Madelin, validation de trimestres... plusieurs leviers selon votre statut.' },
                  { q: 'IS ou IR : que choisir ?', a: 'L\'IS offre plus de flexibilité, l\'IR peut être avantageux pour les faibles revenus.' },
                ].map((faq, i) => (
                  <div key={i}>
                    <h3 className="font-semibold text-[#0b324e] mb-1">{faq.q}</h3>
                    <p className="text-gray-600 text-sm">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0579c4]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Optimisez votre rémunération</h2>
          <p className="text-white/90 mb-8">Étude personnalisée gratuite</p>
          <a href="/#contact" className="btn-white">
            Demander mon étude
            <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
};

const PackLocationSaisonniere = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="bg-[#0b324e] pt-16 pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-semibold tracking-widest uppercase mb-6">
            <Home size={16} /> Pack Premium
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">Location Saisonnière & LMNP</h1>
          <p className="text-white/80 text-lg mb-8">
            Optimisez la fiscalité de vos locations meublées. LMNP, LMP, Airbnb – 
            tirez le meilleur parti du régime réel et des amortissements.
          </p>
          <a href="/#contact" className="btn-white">
            Demander un devis
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#0b324e] mb-6">Pourquoi le régime réel ?</h2>
              <p className="text-gray-600 mb-6">
                Le régime réel permet de déduire toutes vos charges et surtout d'amortir votre bien, 
                ce qui peut réduire considérablement, voire annuler, votre imposition pendant plusieurs années.
              </p>
              <div className="bg-[#0579c4]/10 p-6 mb-6">
                <h3 className="font-bold text-[#0b324e] mb-2">Exemple concret</h3>
                <p className="text-gray-700 text-sm">
                  Un appartement acheté 200 000€ génère environ 6 000€ d'amortissements déductibles par an, 
                  en plus des charges (intérêts, travaux, assurance...). Résultat : souvent 0€ d'impôt pendant 10-15 ans.
                </p>
              </div>
              <ul className="space-y-3">
                {[
                  'Déclaration 2031 et liasses fiscales',
                  'Calcul optimisé des amortissements',
                  'Suivi des déficits reportables',
                  'Gestion multi-biens',
                  'Accompagnement Airbnb, Booking',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#0579c4] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#0b324e] mb-6">Micro-BIC vs Réel</h2>
              <div className="space-y-4">
                <div className="p-4 bg-[#f8fafc] border-l-4 border-gray-300">
                  <h3 className="font-semibold text-[#0b324e]">Micro-BIC</h3>
                  <p className="text-gray-600 text-sm">Abattement forfaitaire de 50%. Simple mais souvent moins avantageux.</p>
                </div>
                <div className="p-4 bg-[#f8fafc] border-l-4 border-[#0579c4]">
                  <h3 className="font-semibold text-[#0b324e]">Régime Réel</h3>
                  <p className="text-gray-600 text-sm">Déduction des charges réelles + amortissements. Plus complexe mais très avantageux.</p>
                </div>
              </div>
              <div className="mt-8 p-6 bg-[#0b324e] text-white">
                <h3 className="font-bold mb-2">Notre conseil</h3>
                <p className="text-white/80 text-sm">
                  Dans 90% des cas, le régime réel est plus avantageux. Contactez-nous pour une simulation gratuite.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0579c4]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Optimisez votre location meublée</h2>
          <p className="text-white/90 mb-8">Simulation gratuite micro-BIC vs réel</p>
          <a href="/#contact" className="btn-white">
            Demander ma simulation
            <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
};

const PackMicroEntreprise = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="bg-[#0b324e] pt-16 pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-semibold tracking-widest uppercase mb-6">
            <FileCheck size={16} /> Pack Premium
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">Micro-Entreprise & Transformation</h1>
          <p className="text-white/80 text-lg mb-8">
            Passage micro → réel, avec ou sans TVA, transformation en société. 
            Optimisez votre statut au bon moment.
          </p>
          <a href="/#contact" className="btn-white">
            Demander un diagnostic
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#0b324e] mb-6">Quand changer de régime ?</h2>
              <p className="text-gray-600 mb-6">
                La micro-entreprise est idéale pour démarrer, mais peut devenir pénalisante 
                quand votre activité se développe. Les seuils à surveiller :
              </p>
              <div className="space-y-4 mb-6">
                <div className="p-4 bg-[#f8fafc]">
                  <h3 className="font-semibold text-[#0b324e]">Seuils de CA (2024)</h3>
                  <ul className="text-gray-600 text-sm mt-2 space-y-1">
                    <li>• Services : 77 700€ (micro-BNC/BIC)</li>
                    <li>• Ventes : 188 700€ (micro-BIC)</li>
                    <li>• TVA : 36 800€ / 91 900€</li>
                  </ul>
                </div>
              </div>
              <h3 className="font-bold text-[#0b324e] mb-3">Raisons de passer au réel :</h3>
              <ul className="space-y-2">
                {[
                  'Charges réelles > abattement forfaitaire',
                  'Besoin de déduire des investissements',
                  'Volonté de séparer patrimoine pro/perso',
                  'Crédibilité auprès des clients/partenaires',
                  'Optimisation de la rémunération',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#0579c4] flex-shrink-0 mt-1" size={18} />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#0b324e] mb-6">Nos accompagnements</h2>
              <div className="space-y-4">
                {[
                  { title: 'Passage micro → réel (EI)', desc: 'Conservez votre entreprise individuelle mais passez au régime réel pour déduire vos charges.' },
                  { title: 'Création de société', desc: 'Transformez votre micro en SASU, EURL ou SARL pour plus de flexibilité.' },
                  { title: 'Gestion de la TVA', desc: 'Franchise, régime simplifié ou réel : nous vous guidons.' },
                  { title: 'Transformation SASU ↔ EURL', desc: 'Changez de forme juridique selon l\'évolution de votre situation.' },
                ].map((item, i) => (
                  <div key={i} className="p-4 bg-[#f8fafc] border-l-4 border-[#0579c4]">
                    <h3 className="font-semibold text-[#0b324e]">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0579c4]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Besoin de changer de statut ?</h2>
          <p className="text-white/90 mb-8">Diagnostic gratuit de votre situation</p>
          <a href="/#contact" className="btn-white">
            Demander mon diagnostic
            <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
};

const PackTableauDeBord = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="bg-[#0b324e] pt-16 pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-semibold tracking-widest uppercase mb-6">
            <BarChart3 size={16} /> Pack Premium
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">Tableau de Bord & Pilotage</h1>
          <p className="text-white/80 text-lg mb-8">
            Pilotez votre entreprise avec des indicateurs clés. Budget, trésorerie, marges, 
            alertes – prenez les bonnes décisions au bon moment.
          </p>
          <a href="/#contact" className="btn-white">
            Demander une démo
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-[#0b324e] mb-6">Ce que vous voyez en temps réel</h2>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Wallet, title: 'Trésorerie', desc: 'Solde actuel et prévisionnel' },
                  { icon: TrendingUp, title: 'CA & Marges', desc: 'Évolution et comparatifs' },
                  { icon: Receipt, title: 'Factures', desc: 'À émettre, en attente, payées' },
                  { icon: PieChart, title: 'Charges', desc: 'Répartition et évolution' },
                  { icon: Calendar, title: 'Échéances', desc: 'Fiscales et sociales' },
                  { icon: Target, title: 'Objectifs', desc: 'Suivi vs prévisionnel' },
                ].map((item, i) => (
                  <div key={i} className="p-4 bg-[#f8fafc]">
                    <item.icon className="w-8 h-8 text-[#0579c4] mb-2" />
                    <h3 className="font-semibold text-[#0b324e]">{item.title}</h3>
                    <p className="text-gray-600 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#0b324e] mb-6">L'accompagnement inclus</h2>
              <ul className="space-y-4">
                {[
                  'Configuration de vos KPIs personnalisés',
                  'RDV mensuel d\'analyse avec votre expert',
                  'Alertes automatiques (seuils, échéances)',
                  'Budget prévisionnel annuel',
                  'Comparatifs N/N-1',
                  'Export et rapports sur demande',
                  'Formation à la lecture des indicateurs',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#0579c4] flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-6 bg-[#0b324e] text-white">
                <h3 className="font-bold mb-2">Grâce à Pennylane</h3>
                <p className="text-white/80 text-sm">
                  Vos données sont synchronisées en temps réel. Plus besoin d'attendre le bilan pour savoir où vous en êtes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0579c4]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Pilotez votre entreprise efficacement</h2>
          <p className="text-white/90 mb-8">Demandez une démonstration personnalisée</p>
          <a href="/#contact" className="btn-white">
            Demander une démo
            <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
};

// ============================================
// CITY PAGE TEMPLATE
// ============================================

const CityPage = ({ city, region, localInfo }) => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="bg-[#0b324e] pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-sm font-semibold tracking-widest uppercase mb-6">
              <MapPin size={16} /> {region}
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Expert-comptable à {city}
              <span className="block text-[#0579c4] mt-2">Conseil fiscal & pilotage pour dirigeants exigeants</span>
            </h1>
            <p className="text-white/80 text-lg mb-8">
              Cabinet 100% digital propulsé par Pennylane. Accompagnement premium pour les entrepreneurs de {city} et {region}.
              Réactivité garantie, trésorerie temps réel, automatisations comptables.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/#contact" className="btn-white">
                Prendre RDV
                <ArrowRight size={18} />
              </a>
              <a href="tel:0789578187" className="btn-secondary text-white border-white hover:bg-white hover:text-[#0b324e]">
                <Phone size={18} />
                07 89 57 81 87
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Local Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0b324e] mb-6">
                Votre expert-comptable digital à {city}
              </h2>
              <p className="text-gray-600 mb-6">
                {localInfo || `Que vous soyez créateur d'entreprise, dirigeant de PME ou investisseur immobilier à ${city}, 
                HT Conseils vous accompagne avec un service 100% digital et personnalisé.`}
              </p>
              <p className="text-gray-600 mb-6">
                Grâce à Pennylane, vous bénéficiez d'une vision temps réel de votre trésorerie, 
                d'automatisations qui simplifient votre quotidien, et d'un accompagnement réactif sous 24-48h.
              </p>
              <ul className="space-y-3">
                {[
                  'Optimisation de la rémunération dirigeant',
                  'Création et transformation de société',
                  'Location meublée (LMNP/LMP)',
                  'Tableaux de bord et pilotage',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="text-[#0579c4] flex-shrink-0" size={20} />
                    <span className="text-[#0b324e]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#f8fafc] p-8">
              <h3 className="text-xl font-bold text-[#0b324e] mb-4">Pourquoi choisir HT Conseils ?</h3>
              <div className="space-y-4">
                {[
                  { icon: Clock, title: 'Réactivité 24-48h', desc: 'Réponses rapides à toutes vos questions' },
                  { icon: Laptop, title: '100% Digital', desc: 'Pas de déplacement, tout se fait en ligne' },
                  { icon: LineChart, title: 'Pennylane', desc: 'Trésorerie temps réel et automatisations' },
                  { icon: Users, title: 'Accompagnement humain', desc: 'Un interlocuteur dédié qui vous connaît' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#0579c4] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#0b324e]">{item.title}</div>
                      <div className="text-sm text-gray-600">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0579c4]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Entrepreneur à {city} ? Parlons de votre projet
          </h2>
          <p className="text-white/90 mb-8">Premier échange gratuit et sans engagement</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/#contact" className="btn-white">
              Prendre rendez-vous
              <ArrowRight size={18} />
            </a>
            <a href="tel:0789578187" className="btn-secondary text-white border-white hover:bg-white hover:text-[#0579c4]">
              <Phone size={18} />
              07 89 57 81 87
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============================================
// LEGAL PAGES - Mentions légales et Politique de confidentialité
// ============================================

const MentionsLegales = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="bg-[#0b324e] pt-16 pb-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white">Mentions Légales</h1>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 prose prose-lg">
          <h2 className="text-2xl font-bold text-[#0b324e] mb-4">1. Éditeur du site</h2>
          <p className="text-gray-600 mb-6">
            Le site htconseils.fr est édité par :<br /><br />
            <strong>HT CONSEILS</strong><br />
            Cabinet d'Expertise Comptable<br />
            3 rue du Commandant Clerc<br />
            67190 Mutzig, France<br /><br />
            Téléphone : 07 89 57 81 87<br />
            Email : contact@htconseils.fr<br /><br />
            SIRET : 850 674 243 00017<br />
            Numéro d'inscription à l'Ordre des Experts-Comptables : 18-00002877-01<br />
            Région : Grand Est
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">2. Directeur de la publication</h2>
          <p className="text-gray-600 mb-6">
            Le directeur de la publication est M. Tuncer, en qualité de représentant légal de HT CONSEILS.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">3. Hébergement</h2>
          <p className="text-gray-600 mb-6">
            Le site est hébergé par :<br /><br />
            <strong>OVH SAS</strong><br />
            2 rue Kellermann<br />
            59100 Roubaix, France<br />
            Téléphone : 1007<br />
            Site web : www.ovh.com
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">4. Propriété intellectuelle</h2>
          <p className="text-gray-600 mb-6">
            L'ensemble du contenu de ce site (textes, images, vidéos, logos, icônes, sons, logiciels, etc.) 
            est la propriété exclusive de HT CONSEILS ou de ses partenaires. Toute reproduction, représentation, 
            modification, publication, adaptation de tout ou partie des éléments du site, quel que soit le moyen 
            ou le procédé utilisé, est interdite, sauf autorisation écrite préalable de HT CONSEILS.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">5. Responsabilité</h2>
          <p className="text-gray-600 mb-6">
            HT CONSEILS s'efforce d'assurer au mieux l'exactitude et la mise à jour des informations 
            diffusées sur ce site. Toutefois, HT CONSEILS ne peut garantir l'exactitude, la précision 
            ou l'exhaustivité des informations mises à disposition sur ce site.<br /><br />
            En conséquence, HT CONSEILS décline toute responsabilité pour toute imprécision, inexactitude 
            ou omission portant sur des informations disponibles sur ce site.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">6. Liens hypertextes</h2>
          <p className="text-gray-600 mb-6">
            Le site peut contenir des liens hypertextes vers d'autres sites. HT CONSEILS n'exerce aucun 
            contrôle sur ces sites et décline toute responsabilité quant à leur contenu.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">7. Droit applicable</h2>
          <p className="text-gray-600 mb-6">
            Les présentes mentions légales sont régies par le droit français. En cas de litige, 
            les tribunaux français seront seuls compétents.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">8. Contact</h2>
          <p className="text-gray-600 mb-6">
            Pour toute question relative aux présentes mentions légales, vous pouvez nous contacter à 
            l'adresse suivante : contact@htconseils.fr
          </p>

          <p className="text-gray-500 text-sm mt-12">
            Dernière mise à jour : Janvier 2026
          </p>
        </div>
      </section>
    </div>
  );
};

const PolitiqueConfidentialite = () => {
  return (
    <div className="min-h-screen pt-20">
      <section className="bg-[#0b324e] pt-16 pb-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white">Politique de Confidentialité</h1>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 prose prose-lg">
          <p className="text-gray-600 mb-6">
            La présente politique de confidentialité décrit comment HT CONSEILS collecte, utilise et protège 
            les données personnelles que vous nous fournissez via notre site web htconseils.fr.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">1. Responsable du traitement</h2>
          <p className="text-gray-600 mb-6">
            Le responsable du traitement des données personnelles est :<br /><br />
            <strong>HT CONSEILS</strong><br />
            3 rue du Commandant Clerc<br />
            67190 Mutzig, France<br />
            Email : contact@htconseils.fr
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">2. Données collectées</h2>
          <p className="text-gray-600 mb-6">
            Nous collectons les données personnelles suivantes :<br /><br />
            <strong>Via le formulaire de contact :</strong>
          </p>
          <ul className="list-disc list-inside text-gray-600 mb-6">
            <li>Nom et prénom</li>
            <li>Adresse email</li>
            <li>Numéro de téléphone (optionnel)</li>
            <li>Nom de l'entreprise (optionnel)</li>
            <li>Message</li>
          </ul>
          <p className="text-gray-600 mb-6">
            <strong>Données de navigation :</strong> Nous pouvons également collecter des données de navigation 
            (adresse IP, type de navigateur, pages visitées) via des cookies à des fins statistiques.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">3. Finalités du traitement</h2>
          <p className="text-gray-600 mb-6">
            Vos données personnelles sont collectées pour les finalités suivantes :
          </p>
          <ul className="list-disc list-inside text-gray-600 mb-6">
            <li>Répondre à vos demandes de contact et de renseignements</li>
            <li>Vous recontacter dans le cadre de nos services d'expertise comptable</li>
            <li>Améliorer notre site web et nos services</li>
            <li>Établir des statistiques de fréquentation</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">4. Base légale du traitement</h2>
          <p className="text-gray-600 mb-6">
            Le traitement de vos données repose sur :
          </p>
          <ul className="list-disc list-inside text-gray-600 mb-6">
            <li>Votre consentement (formulaire de contact)</li>
            <li>L'exécution de mesures précontractuelles</li>
            <li>Notre intérêt légitime (amélioration de nos services)</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">5. Destinataires des données</h2>
          <p className="text-gray-600 mb-6">
            Vos données personnelles sont destinées uniquement à HT CONSEILS et ne sont pas transmises 
            à des tiers, sauf obligation légale. Elles peuvent être traitées par nos sous-traitants 
            techniques (hébergeur, outil de messagerie) dans le respect du RGPD.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">6. Durée de conservation</h2>
          <p className="text-gray-600 mb-6">
            Vos données personnelles sont conservées pendant une durée de 3 ans à compter de votre dernier 
            contact avec nous, sauf si une relation contractuelle est établie (auquel cas les durées légales 
            de conservation comptable s'appliquent).
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">7. Vos droits</h2>
          <p className="text-gray-600 mb-6">
            Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :
          </p>
          <ul className="list-disc list-inside text-gray-600 mb-6">
            <li><strong>Droit d'accès :</strong> obtenir la confirmation que des données vous concernant sont traitées et en obtenir une copie</li>
            <li><strong>Droit de rectification :</strong> demander la correction de données inexactes</li>
            <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
            <li><strong>Droit à la limitation :</strong> demander la limitation du traitement</li>
            <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
            <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
          </ul>
          <p className="text-gray-600 mb-6">
            Pour exercer ces droits, contactez-nous à : contact@htconseils.fr<br /><br />
            Vous disposez également du droit d'introduire une réclamation auprès de la CNIL 
            (Commission Nationale de l'Informatique et des Libertés).
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">8. Cookies</h2>
          <p className="text-gray-600 mb-6">
            Notre site utilise des cookies pour améliorer votre expérience de navigation et établir 
            des statistiques de fréquentation. Vous pouvez à tout moment paramétrer votre navigateur 
            pour refuser les cookies.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">9. Sécurité</h2>
          <p className="text-gray-600 mb-6">
            Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger 
            vos données personnelles contre la destruction accidentelle ou illicite, la perte, l'altération, 
            la divulgation ou l'accès non autorisé.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">10. Modification de la politique</h2>
          <p className="text-gray-600 mb-6">
            Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. 
            Les modifications entreront en vigueur dès leur publication sur le site.
          </p>

          <h2 className="text-2xl font-bold text-[#0b324e] mb-4 mt-8">11. Contact</h2>
          <p className="text-gray-600 mb-6">
            Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits, 
            contactez-nous :<br /><br />
            Email : contact@htconseils.fr<br />
            Téléphone : 07 89 57 81 87<br />
            Adresse : 3 rue du Commandant Clerc, 67190 Mutzig
          </p>

          <p className="text-gray-500 text-sm mt-12">
            Dernière mise à jour : Janvier 2026
          </p>
        </div>
      </section>
    </div>
  );
};

// ============================================
// SCROLL TO TOP
// ============================================

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// ============================================
// MAIN APP
// ============================================

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="App">
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/expert-comptable-en-ligne" element={<ExpertComptableEnLignePage />} />
          
          {/* City Pages */}
          <Route path="/expert-comptable-paris" element={<CityPage city="Paris" region="Île-de-France" localInfo="Capitale économique de la France, Paris regorge d'entrepreneurs ambitieux. Que vous lanciez votre startup dans le Marais ou que vous gériez une PME dans le 8ème, nous vous accompagnons à distance avec la même exigence qu'un cabinet parisien." />} />
          <Route path="/expert-comptable-lyon" element={<CityPage city="Lyon" region="Auvergne-Rhône-Alpes" localInfo="2ème métropole économique de France, Lyon est un vivier d'entrepreneurs innovants. Notre accompagnement digital s'adapte parfaitement au dynamisme de la région." />} />
          <Route path="/expert-comptable-marseille" element={<CityPage city="Marseille" region="Provence-Alpes-Côte d'Azur" localInfo="Port méditerranéen et pôle économique majeur, Marseille attire de nombreux entrepreneurs. Notre modèle 100% digital vous permet de bénéficier d'un accompagnement premium où que vous soyez." />} />
          <Route path="/expert-comptable-strasbourg" element={<CityPage city="Strasbourg" region="Alsace - Grand Est" localInfo="Capitale européenne et cœur économique de l'Alsace, Strasbourg est notre région d'origine. Nous connaissons parfaitement le tissu économique local et les spécificités de la région." />} />
          <Route path="/expert-comptable-bordeaux" element={<CityPage city="Bordeaux" region="Nouvelle-Aquitaine" localInfo="Métropole dynamique et attractive, Bordeaux séduit de nombreux entrepreneurs. Notre accompagnement digital vous suit partout." />} />
          <Route path="/expert-comptable-toulouse" element={<CityPage city="Toulouse" region="Occitanie" localInfo="Capitale de l'aéronautique et ville étudiante, Toulouse est un terreau fertile pour les entrepreneurs. Nous vous accompagnons dans vos projets." />} />
          <Route path="/expert-comptable-nantes" element={<CityPage city="Nantes" region="Pays de la Loire" localInfo="Ville créative et entreprenante, Nantes attire les talents. Notre expertise 100% digitale s'adapte à votre dynamisme." />} />
          <Route path="/expert-comptable-lille" element={<CityPage city="Lille" region="Hauts-de-France" localInfo="Carrefour européen et métropole du Nord, Lille est un hub économique majeur. Bénéficiez de notre accompagnement premium à distance." />} />
          <Route path="/expert-comptable-vallee-bruche" element={<CityPage city="Vallée de la Bruche" region="Alsace - Grand Est" localInfo="Notre berceau ! Implantés à Mutzig, nous connaissons intimement le tissu économique de la vallée de la Bruche. Artisans, commerçants, professions libérales : nous sommes vos voisins et vos partenaires de confiance." />} />
          
          {/* Pack Pages */}
          <Route path="/pack/creation-entreprise" element={<PackCreationEntreprise />} />
          <Route path="/pack/optimisation-remuneration" element={<PackOptimisationRemuneration />} />
          <Route path="/pack/location-saisonniere" element={<PackLocationSaisonniere />} />
          <Route path="/pack/micro-entreprise" element={<PackMicroEntreprise />} />
          <Route path="/pack/tableau-de-bord" element={<PackTableauDeBord />} />
          
          {/* Legal Pages */}
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
        </Routes>
        <Footer />
        <WhatsAppButton />
      </div>
    </BrowserRouter>
  );
}

export default App;
